import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import PageHeader from '@/components/layout/PageHeader';
import Button from '@/components/ui/Button';
import ConfirmModal from '@/components/ui/ConfirmModal';
import { useGlobalDirtyGuard } from '@/context/DirtyNavigationProvider';

export interface FootballFormFrameProps {
  title: string;
  eyebrow?: string;
  errors: Record<string, string[]>;
  dirty: boolean;
  submitting: boolean;
  cancelHref: string;
  submitLabel: string;
  onSubmit: React.FormEventHandler<HTMLFormElement>;
  sections: React.ReactNode;
  guardKey?: string;
  guardLabel?: string;
  canSubmit?: boolean;
}

const FootballFormFrame: React.FC<FootballFormFrameProps> = ({
  title,
  eyebrow,
  errors,
  dirty,
  submitting,
  cancelHref,
  submitLabel,
  onSubmit,
  sections,
  guardKey,
  guardLabel = 'form changes',
  canSubmit = true,
}) => {
  const navigate = useNavigate();
  const summaryRef = useRef<HTMLDivElement>(null);
  const hadErrorsRef = useRef(false);
  const [confirmingCancel, setConfirmingCancel] = useState(false);
  useGlobalDirtyGuard(guardKey || 'inactive-football-form', Boolean(guardKey && dirty), guardLabel);
  const errorEntries = Object.entries(errors).flatMap(([field, messages]) =>
    messages.map((message) => ({ field, message })),
  );
  const hasErrors = errorEntries.length > 0;

  useEffect(() => {
    if (hasErrors && !hadErrorsRef.current) summaryRef.current?.focus();
    hadErrorsRef.current = hasErrors;
  }, [hasErrors]);

  return (
    <div className="mx-auto max-w-5xl min-w-0 space-y-6">
      <PageHeader title={title} eyebrow={eyebrow} />
      <form
        noValidate
        onSubmit={onSubmit}
        className="space-y-6 rounded-xl bg-white p-5 shadow-sm ring-1 ring-gray-200 dark:bg-gray-900 dark:ring-gray-800 sm:p-6"
      >
        {errorEntries.length > 0 && (
          <div
            ref={summaryRef}
            role="alert"
            tabIndex={-1}
            className="rounded-lg border-l-4 border-red-600 bg-red-50 p-4 text-red-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-600 dark:bg-red-950/50 dark:text-red-100"
          >
            <h2 className="font-semibold">Check the highlighted fields</h2>
            <ul className="mt-2 list-disc space-y-1 pl-5 text-sm">
              {errorEntries.map(({ field, message }, index) => (
                <li key={`${field}-${index}`}>
                  <a className="underline hover:no-underline" href={`#${field}`}>{message}</a>
                </li>
              ))}
            </ul>
          </div>
        )}
        <div className="space-y-6">{sections}</div>
        <div className="flex flex-col-reverse gap-3 border-t border-gray-200 pt-5 dark:border-gray-700 sm:flex-row sm:justify-end">
          <Link
            to={cancelHref}
            onClick={(event) => {
              if (!dirty || guardKey) return;
              event.preventDefault();
              setConfirmingCancel(true);
            }}
            className="inline-flex min-h-11 items-center justify-center rounded-md border border-gray-300 px-4 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-500 focus-visible:ring-offset-2 motion-reduce:transition-none dark:border-gray-700 dark:text-gray-200 dark:hover:bg-gray-800 dark:focus-visible:ring-offset-gray-900"
          >
            Cancel
          </Link>
          {canSubmit && <Button
            type="submit"
            loading={submitting}
            loadingLabel={`${submitLabel} in progress`}
            className="min-h-11 motion-reduce:transition-none"
          >
            {submitLabel}
          </Button>}
        </div>
      </form>
      <ConfirmModal
        isOpen={confirmingCancel}
        title="Discard changes?"
        message="Your unsaved changes will be lost."
        confirmLabel="Discard changes"
        cancelLabel="Keep editing"
        tone="danger"
        onClose={() => setConfirmingCancel(false)}
        onConfirm={() => {
          setConfirmingCancel(false);
          navigate(cancelHref);
        }}
      />
    </div>
  );
};

export default FootballFormFrame;
